import type {
	AddressBookContactDetail,
	AddressBookItemSummary,
} from "~/shared/schemas/address-book-schema.ts";
import type {
	Country,
	ZipCode,
} from "~/shared/schemas/location-state-schema.ts";
import { formatPhoneNumber } from "~/shared/utils/string-fns.ts";
import { normalizeHoursToApiFormat } from "~/shared/utils/time-utils.ts";

export type LocationValue = {
	postalCode: string;
	city: string;
	state: string;
	country: string;
};

export type StopDetailsValue = {
	addressBookItemId?: number | null;
	stopName?: string | null;
	contactName?: string | null;
	contactPhone?: string | null;
	contactExtension?: string | null;
	address1?: string | null;
	address2?: string | null;
	hoursOpen?: string | null;
	hoursClose?: string | null;
	isLegacy?: boolean | null;
	puNumber?: string | null;
	deliveryPO?: string | null;
	location?: LocationValue;
};

type StopTypeName = "pickup" | "delivery";
type StopTypeId = 1 | 2;
type NullableStopTypeId = StopTypeId | null;

const canonicalCountryByAlias: Record<string, "USA" | "CAN" | "MEX"> = {
	US: "USA",
	USA: "USA",
	PR: "USA",
	PUERTORICO: "USA",
	UNITEDSTATES: "USA",
	UNITEDSTATESOFAMERICA: "USA",
	CA: "CAN",
	CAN: "CAN",
	CANADA: "CAN",
	MX: "MEX",
	MEX: "MEX",
	MEXICO: "MEX",
};

const normalizeCountryAliasKey = (value: string): string =>
	value
		.trim()
		.toUpperCase()
		.replaceAll(/[^A-Z]/g, "");

export const normalizeCountryCode = (
	value: string | null | undefined,
): string | null | undefined => {
	if (value == null) {
		return value;
	}

	const trimmed = value.trim();
	if (trimmed === "") {
		return value;
	}

	const aliasKey = normalizeCountryAliasKey(trimmed);
	return canonicalCountryByAlias[aliasKey] ?? trimmed;
};

export const deriveCountry = (current: Country, value?: string) => {
	if (!value || value.trim() === "") {
		return current;
	}

	const normalizedValue = normalizeCountryCode(value) ?? value;

	return {
		name: current.name || normalizedValue,
		abbreviation: normalizedValue,
	};
};

export const updatePostalCode = (
	current: ZipCode,
	location?: LocationValue,
) => {
	if (!location) {
		return current;
	}

	const stateValue = location.state?.trim() ?? "";
	const countryValue = location.country?.trim() ?? "";

	return {
		...current,
		id: location.postalCode ?? current.id,
		postalCode: location.postalCode ?? current.postalCode,
		city: {
			...current.city,
			name: location.city ?? current.city.name,
			state: {
				...current.city.state,
				abbreviation: stateValue || current.city.state.abbreviation,
				name: current.city.state.name || stateValue,
				countryCode: countryValue || current.city.state.countryCode,
				country: {
					...current.city.state.country,
					abbreviation: countryValue || current.city.state.country.abbreviation,
					name: current.city.state.country.name || countryValue,
				},
			},
		},
	};
};

export const mapStopDetailsToAddressBookItem = (
	stopDetails: StopDetailsValue | null | undefined,
	stopType: StopTypeName,
	location?: LocationValue,
): AddressBookItemSummary | null => {
	if (!stopDetails) return null;

	const resolvedLocation = stopDetails.location ?? location;

	return {
		addressBookItemId: stopDetails.addressBookItemId ?? 0,
		companyName: stopDetails.stopName ?? "",
		address1: stopDetails.address1 ?? "",
		address2: stopDetails.address2 ?? "",
		postalCode: resolvedLocation?.postalCode ?? "",
		city: resolvedLocation?.city ?? "",
		stateProvince: resolvedLocation?.state ?? "",
		country: resolvedLocation?.country ?? "",
		createdDate: "",
		createdByCustomerId: null,
		createdByInternalId: null,
		customerId: 0,
		isValidated: null,
		legacyAddressId: null,
		isLegacy: stopDetails.isLegacy ?? false,
		rowVersion: null,
		details: [
			{
				addressBookItemId: 0,
				rowVersion: null,
				contactDetailId: 0,
				contactName: stopDetails.contactName ?? "",
				// Bulk-uploaded stops arrive as raw digits while typed values are
				// already masked. Normalizing at hydration (not on every render)
				// keeps the display consistent without fighting the masked input's
				// caret while the user types.
				contactPhone: formatPhoneNumber(stopDetails.contactPhone ?? ""),
				contactExtension: stopDetails.contactExtension ?? "",
				hoursOpen: stopDetails.hoursOpen ?? "",
				hoursClose: stopDetails.hoursClose ?? "",
				stopType: stopType === "pickup" ? 1 : 2,
				mode: 0,
				timeZone: "",
				specialInstructions: null,
				accessorials: null,
				puNumber: stopDetails.puNumber ?? undefined,
				deliveryPO: stopDetails.deliveryPO ?? undefined,
			} as AddressBookContactDetail & {
				puNumber?: string;
				deliveryPO?: string;
			},
		],
	};
};

const normalize = (value: string | null | undefined) =>
	(value ?? "").trim().toLowerCase();

const normalizeCountry = (value: string | null | undefined) =>
	normalize(normalizeCountryCode(value));

const normalizePhone = (value: string | null | undefined) => {
	const normalized = normalize(value);
	const digits = normalized.replaceAll(/\D/g, "");
	return digits || normalized;
};

const normalizeHours = (value: string | null | undefined) =>
	normalize(normalizeHoursToApiFormat(value));

const stopTypeToId = (stopType?: StopTypeName): NullableStopTypeId => {
	if (stopType === "pickup") return 1;
	if (stopType === "delivery") return 2;
	return null;
};

/**
 * Narrows the candidate list using the supplied filter. Behaviour:
 * - if exactly one candidate matches, return that single-item list
 * - if more than one match, return the narrowed list
 * - if none match, return the original list (don't narrow on a non-match)
 */
const narrow = (
	candidates: AddressBookItemSummary[],
	predicate: (item: AddressBookItemSummary) => boolean,
): AddressBookItemSummary[] => {
	const next = candidates.filter((item) => predicate(item));
	if (next.length === 0) return candidates;
	return next;
};

/**
 * Applies contact-detail-based tiebreakers (contact name/phone, then
 * hours open/close) for the matching `stopTypeId`.
 */
const narrowByContactDetails = (
	matches: AddressBookItemSummary[],
	stopDetails: StopDetailsValue,
	stopTypeId: StopTypeId,
): AddressBookItemSummary[] => {
	let next = matches;

	const targetContactName = normalize(stopDetails.contactName);
	const targetContactPhone = normalize(stopDetails.contactPhone);
	if (targetContactName || targetContactPhone) {
		next = narrow(next, (item) =>
			(item.details ?? []).some(
				(d) =>
					d.stopType === stopTypeId &&
					(!targetContactName ||
						normalize(d.contactName) === targetContactName) &&
					(!targetContactPhone ||
						normalize(d.contactPhone) === targetContactPhone),
			),
		);
		if (next.length === 1) return next;
	}

	const targetHoursOpen = normalize(stopDetails.hoursOpen);
	const targetHoursClose = normalize(stopDetails.hoursClose);
	if (targetHoursOpen || targetHoursClose) {
		next = narrow(next, (item) =>
			(item.details ?? []).some(
				(d) =>
					d.stopType === stopTypeId &&
					(!targetHoursOpen || normalize(d.hoursOpen) === targetHoursOpen) &&
					(!targetHoursClose || normalize(d.hoursClose) === targetHoursClose),
			),
		);
	}

	return next;
};

const getStopTypeDetail = (
	item: AddressBookItemSummary,
	stopTypeId: NullableStopTypeId,
) => {
	if (stopTypeId === null) return undefined;
	return item.details?.find((detail) => detail.stopType === stopTypeId);
};

const getEquivalentDuplicateKey = (
	item: AddressBookItemSummary,
	stopTypeId: NullableStopTypeId,
) => {
	const detail = getStopTypeDetail(item, stopTypeId);
	return JSON.stringify([
		normalize(item.companyName),
		normalize(item.address1),
		normalize(item.address2),
		normalize(item.postalCode),
		normalize(item.city),
		normalize(item.stateProvince),
		normalize(item.country),
		normalize(detail?.contactName),
		normalize(detail?.contactPhone),
		normalize(detail?.contactExtension),
		normalize(detail?.hoursOpen),
		normalize(detail?.hoursClose),
		normalize(detail?.accessorials),
		normalize(detail?.specialInstructions),
	]);
};

const areEquivalentDuplicates = (
	matches: AddressBookItemSummary[],
	stopTypeId: NullableStopTypeId,
) => {
	const firstMatch = matches[0];
	if (!firstMatch) return false;

	const duplicateKey = getEquivalentDuplicateKey(firstMatch, stopTypeId);
	return matches.every(
		(item) => getEquivalentDuplicateKey(item, stopTypeId) === duplicateKey,
	);
};

const getOldestAddressBookItem = (matches: AddressBookItemSummary[]) =>
	[...matches].sort((a, b) => a.addressBookItemId - b.addressBookItemId)[0] ??
	null;

const findLocationMatches = (
	items: AddressBookItemSummary[] | null | undefined,
	stopDetails: StopDetailsValue | null | undefined,
	location?: LocationValue,
): AddressBookItemSummary[] => {
	if (!items?.length || !stopDetails) return [];

	const targetAddress1 = normalize(stopDetails.address1);
	if (!targetAddress1) return [];

	const resolvedLocation = stopDetails.location ?? location;
	const targetPostal = normalize(resolvedLocation?.postalCode);
	const targetCity = normalize(resolvedLocation?.city);
	const targetState = normalize(resolvedLocation?.state);
	const targetCountry = normalizeCountry(resolvedLocation?.country);
	if (!targetPostal || !targetCity || !targetState || !targetCountry) return [];

	const targetAddress2 = normalize(stopDetails.address2);
	const targetStopName = normalize(stopDetails.stopName);

	return items.filter(
		(item) =>
			normalize(item.address1) === targetAddress1 &&
			normalize(item.address2) === targetAddress2 &&
			normalize(item.postalCode) === targetPostal &&
			normalize(item.city) === targetCity &&
			normalize(item.stateProvince) === targetState &&
			normalizeCountry(item.country) === targetCountry &&
			normalize(item.companyName) === targetStopName,
	);
};

const matchesStopDetailsExactly = (
	detail: AddressBookContactDetail,
	stopDetails: StopDetailsValue,
	stopTypeId: StopTypeId,
) =>
	detail.stopType === stopTypeId &&
	normalize(detail.contactName) === normalize(stopDetails.contactName) &&
	normalizePhone(detail.contactPhone) ===
		normalizePhone(stopDetails.contactPhone) &&
	normalize(detail.contactExtension) ===
		normalize(stopDetails.contactExtension) &&
	normalizeHours(detail.hoursOpen) === normalizeHours(stopDetails.hoursOpen) &&
	normalizeHours(detail.hoursClose) === normalizeHours(stopDetails.hoursClose);

/**
 * Conservatively infers a saved address for quote responses that do not carry
 * address-book IDs. Both the parent location and the applicable stop detail
 * must match; otherwise the quote-derived stop remains detached.
 */
export const findExactMatchingAddressBookItem = (
	items: AddressBookItemSummary[] | null | undefined,
	stopDetails: StopDetailsValue | null | undefined,
	location: LocationValue | undefined,
	stopType: StopTypeName,
): AddressBookItemSummary | null => {
	if (!stopDetails) return null;

	const stopTypeId = stopTypeToId(stopType);
	if (stopTypeId === null) return null;

	const matches = findLocationMatches(items, stopDetails, location).filter(
		(item) =>
			item.details.some((detail) =>
				matchesStopDetailsExactly(detail, stopDetails, stopTypeId),
			),
	);

	if (matches.length <= 1) return matches[0] ?? null;
	if (!areEquivalentDuplicates(matches, stopTypeId)) return null;

	return getOldestAddressBookItem(matches);
};

/**
 * Finds an address book item that is an unambiguous match for the stop details.
 *
 * Requires an exact (case-insensitive, trimmed) match on **all** of:
 *   address1, address2, postalCode, city, stateProvince, country,
 *   companyName === stopName
 *
 * address2 is an identity field, so it is always compared: a blank quote
 * address2 only matches a blank saved address2, and clearing a saved address's
 * Suite/Unit is treated as a mismatch (so it does not silently reattach on
 * reload).
 *
 * The stop name must match the saved company name so that a stop whose name was
 * edited after applying a saved address does not re-match the original item
 * (which would revert the edited name and re-show it as a saved address on
 * quote load).
 *
 * If multiple items match those fields (duplicates with the same address + name
 * are common), tiebreakers are applied in this order — narrowing the candidate
 * set each time and stopping as soon as a single candidate remains:
 *   1. contactName + contactPhone for the matching stopType
 *   2. hoursOpen + hoursClose for the matching stopType
 *
 * If multiple items still match after all tiebreakers and are equivalent for
 * the stop, the oldest one (lowest `addressBookItemId`) is returned so the UI
 * shows a saved address rather than falling back to just the postal code.
 *
 * Returns `null` when:
 * - any required field is empty on the quote
 * - no item matches all required fields (including the stop name)
 * - more than one non-equivalent item still matches after all tiebreakers
 */
export const findMatchingAddressBookItem = (
	items: AddressBookItemSummary[] | null | undefined,
	stopDetails: StopDetailsValue | null | undefined,
	location?: LocationValue,
	stopType?: StopTypeName,
): AddressBookItemSummary | null => {
	if (!stopDetails) return null;

	let matches = findLocationMatches(items, stopDetails, location);

	if (matches.length <= 1) return matches[0] ?? null;

	// Remaining candidates share the same address + name; disambiguate using
	// contact details (then hours) for the matching stopType.
	const stopTypeId = stopTypeToId(stopType);
	if (stopTypeId !== null) {
		matches = narrowByContactDetails(matches, stopDetails, stopTypeId);
		if (matches.length === 1) return matches[0] ?? null;
	}

	if (!areEquivalentDuplicates(matches, stopTypeId)) return null;

	return getOldestAddressBookItem(matches);
};

/**
 * Re-verifies a reconstructed stop's `addressBookItemId` against the customer's
 * current saved addresses. The stored id is trusted only when the stop's address
 * still exactly matches a saved entry (the same name-strict rule used on quote
 * reload); otherwise the stop is detached (id 0) while keeping its typed data.
 *
 * This keeps a stale id — e.g. a template saved from an edited stop before the
 * detach-on-edit invariant existed, or a saved address that has since changed or
 * been deleted — from re-showing an edited stop as a pristine saved address.
 *
 * When the stop still matches a saved address, that saved address's special
 * instructions (stored per stop-type on its details) are carried over onto the
 * returned item. Templates never persist special instructions, and the stop is
 * reconstructed from template JSON with empty details, so this is the only path
 * that can surface the instructions tied to a saved address for the shipment
 * details auto-hydration when a template is applied.
 *
 * Verification is skipped (the item is returned unchanged) when no address book
 * is supplied — e.g. it has not loaded yet — so an applied stop is never falsely
 * detached before its saved addresses are available.
 */
export const verifyStopAddressBookId = (
	item: AddressBookItemSummary | null,
	stopDetails: StopDetailsValue | null | undefined,
	location: LocationValue | undefined,
	stopType: "pickup" | "delivery",
	addressBook: AddressBookItemSummary[] | null | undefined,
): AddressBookItemSummary | null => {
	if (!item || item.addressBookItemId === 0 || addressBook == null) {
		return item;
	}
	const match = findMatchingAddressBookItem(
		addressBook,
		stopDetails,
		location,
		stopType,
	);
	const verified: AddressBookItemSummary = {
		...item,
		addressBookItemId: match?.addressBookItemId ?? 0,
	};

	const stopTypeId = stopTypeToId(stopType);
	const matchedInstructions = match?.details?.find(
		(detail) => detail.stopType === stopTypeId,
	)?.specialInstructions;

	if (matchedInstructions && Array.isArray(verified.details)) {
		verified.details = verified.details.map((detail) =>
			detail.stopType === stopTypeId
				? { ...detail, specialInstructions: matchedInstructions }
				: detail,
		);
	}

	return verified;
};
